/** The zone assumed when an event (or its location) has no timezone set. */
export const DEFAULT_TIMEZONE = 'Australia/Sydney';

/**
 * Normalises a `location.timezone` read (a free-text column, `not null
 * default 'Australia/Sydney'`) to a usable IANA zone name. `null`/`undefined`
 * (no location, or the column genuinely absent from a projection) and an
 * empty or whitespace-only string all fall back to `DEFAULT_TIMEZONE` — the
 * same rule the RPCs apply via SQL's `nullif(l.timezone, '')`. Anything else
 * passes through verbatim, untrimmed.
 */
export function normaliseTimezone(raw: string | null | undefined): string {
  return raw && raw.trim() !== '' ? raw : DEFAULT_TIMEZONE;
}

/** UTC offset (minutes east) and short abbreviation of `zone` at `instant`, via Intl — no tz library. Throws on an unknown zone. */
export function zoneOffsetAt(zone: string, instant: Date): { offsetMin: number; abbrev: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'short',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const asUtc = Date.UTC(
    +get('year'),
    +get('month') - 1,
    +get('day'),
    +get('hour') % 24,
    +get('minute'),
    +get('second'),
  );
  const offsetMin = Math.round((asUtc - instant.getTime()) / 60000);
  const abbrev =
    new Intl.DateTimeFormat('en-AU', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(instant)
      .find((p) => p.type === 'timeZoneName')?.value ?? 'UTC';
  return { offsetMin, abbrev };
}

/** `zoneOffsetAt`, but an unknown zone (free-text column) yields UTC instead of throwing. */
export function zoneOffsetAtSafe(
  zone: string,
  instant: Date,
): { offsetMin: number; abbrev: string } {
  try {
    return zoneOffsetAt(zone, instant);
  } catch {
    return { offsetMin: 0, abbrev: 'UTC' };
  }
}
